import { textToSpeech, speechToText } from "./api";
import AudioRecordingService from "./AudioRecordingService";

export interface ListeningResult {
  transcript: string;
  audioBlob: Blob | null;
}

class SpeechService {
  private isInitialized: boolean = true; // Always initialized since we use API
  private isListening: boolean = false;
  private listeningPromise: Promise<ListeningResult> | null = null;
  private listeningResolve: ((value: ListeningResult) => void) | null = null;
  private readonly audioRecordingService: AudioRecordingService =
    new AudioRecordingService();
  private currentVoiceName?: string;

  async initialize(): Promise<void> {
    // No initialization needed since we use backend API
    this.isInitialized = true;
    console.log("Speech Service geïnitialiseerd (via backend API)");
  }

  setVoiceName(voiceName: string): void {
    this.currentVoiceName = voiceName;
  }

  async startListening(): Promise<ListeningResult> {
    if (!this.isInitialized) {
      throw new Error("Speech Service niet geïnitialiseerd");
    }

    if (this.isListening) {
      throw new Error("Al aan het luisteren");
    }

    this.isListening = true;
    let audioBlob: Blob | null = null;

    this.listeningPromise = new Promise<ListeningResult>((resolve) => {
      this.listeningResolve = resolve;

      // Start recording audio with higher quality settings for better transcription
      this.audioRecordingService.startRecording({
        mimeType: "audio/webm;codecs=opus",
        audioBitsPerSecond: 128000  // Higher quality for better transcription
      }).catch((error) => {
        console.warn(
          "Kon audio recording niet starten, gaat door zonder recording:",
          error
        );
      });

      console.log("Start spraakherkenning (handmatig stoppen vereist)...");
    });

    // Store final resolve function for use in stopListening
    const finalizeResult = async (): Promise<void> => {
      this.isListening = false;
      
      // Stop recording and get audio blob
      if (this.audioRecordingService.getRecordingState()) {
        try {
          const recordingResult = await this.audioRecordingService.stopRecording();
          audioBlob = recordingResult.audioBlob;
          
          // Use backend API to transcribe audio
          if (audioBlob && audioBlob.size > 0) {
            try {
              console.log(`Sending audio blob for transcription: ${audioBlob.size} bytes, type: ${audioBlob.type}, duration: ${recordingResult.duration}ms`);
              
              // Only attempt transcription if we have meaningful audio (more than 1 second and reasonable size)
              if (recordingResult.duration > 1000 && audioBlob.size > 1000) {
                const transcriptionResult = await speechToText(audioBlob);
                
                console.log("Transcription result:", transcriptionResult);
                
                if (this.listeningResolve) {
                  this.listeningResolve({
                    transcript: transcriptionResult.transcript || "",
                    audioBlob,
                  });
                  this.listeningResolve = null;
                }
                return;
              } else {
                console.warn("Audio too short or small for transcription:", {
                  duration: recordingResult.duration,
                  size: audioBlob.size
                });
              }
            } catch (transcriptionError) {
              console.error("Transcription error:", transcriptionError);
              
              // Provide more specific error information
              if (transcriptionError instanceof Error) {
                console.error("Error details:", {
                  message: transcriptionError.message,
                  stack: transcriptionError.stack,
                  audioBlobSize: audioBlob.size,
                  audioBlobType: audioBlob.type,
                  duration: recordingResult.duration
                });
              }
            }
          } else {
            console.warn("No audio blob available for transcription or blob is empty");
          }
        } catch (error) {
          console.warn("Fout bij stoppen audio recording:", error);
        }
      }

      // Fallback if transcription failed
      if (this.listeningResolve) {
        this.listeningResolve({
          transcript: "",
          audioBlob,
        });
        this.listeningResolve = null;
      }
    };

    // Store the finalize function for external access
    (this.listeningPromise as any).finalize = finalizeResult;

    return this.listeningPromise;
  }

  async stopListening(): Promise<void> {
    if (!this.isListening) {
      return;
    }

    console.log("Stop spraakherkenning handmatig...");
    
    // Finalize the result
    if (this.listeningPromise && (this.listeningPromise as any).finalize) {
      await (this.listeningPromise as any).finalize();
    }
  }

  async speak(text: string): Promise<{
    audioBlob: Blob | null;
  }> {
    if (!this.isInitialized) {
      throw new Error("Speech Service niet geïnitialiseerd");
    }

    try {
      console.log("Start spraaksynthese voor:", text, "met stem:", this.currentVoiceName);

      // Use backend API to convert text to speech
      const audioBlob = await textToSpeech(text, this.currentVoiceName);

      // Play the audio
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      
      await new Promise<void>((resolve, reject) => {
        audio.onended = () => {
          URL.revokeObjectURL(audioUrl);
          resolve();
        };
        audio.onerror = () => {
          URL.revokeObjectURL(audioUrl);
          reject(new Error("Audio playback fout"));
        };
        audio.play().catch(reject);
      });

      console.log("Spraaksynthese voltooid");
      
      return { audioBlob };
    } catch (error) {
      console.error("Spraaksynthese fout:", error);
      throw new Error(`Spraaksynthese fout: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  cleanup(): void {
    try {
      // Clean up audio recording service
      if (this.audioRecordingService) {
        this.audioRecordingService.dispose();
      }

      this.isInitialized = false;

      console.log("Speech Service opgeschoond");
    } catch (error) {
      console.error("Fout bij opschonen Speech Service:", error);
    }
  }
}

export default SpeechService;
